import { useEffect, useMemo, useState } from 'react';

import { skipToken } from '@reduxjs/toolkit/query';
import { useGetProjectsQuery, useSelectedProjectId } from 'features/projects';
import { Plus, Settings } from 'lucide-react';
import { useDebouncedValue } from 'shared/hooks/useDebouncedValue';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import UserConfigDialog, { type EditUserDialogVariant } from '../components/UserConfigDialog';
import { UsersInput as Input } from '../components/ui/UsersInput';
import { useGetUsersListingQuery, UsersListingParams } from '../services/users.api';
import { UserListItem } from '../types/user';

type ActiveFilter = 'active' | 'inactive' | 'all';
type Mode = 'project' | 'admins';

const SUBPROJECT_ALL_VALUE = 'all';
const SKELETON_ROW_KEYS = ['s1', 's2', 's3', 's4', 's5', 's6'];
const SUB_PROJECTS_VISIBLE = 2;

const SubProjectChip = ({ name }: { name: string }) => (
  <span className="text-xs px-2 py-0.5 rounded-full bg-bg-surface-alt text-text-muted">{name}</span>
);

const SubProjectsCell = ({ subProjects }: { subProjects: UserListItem['subProjects'] }) => {
  if (subProjects.length === 0) return <span className="text-text-subtle">—</span>;

  const visible = subProjects.slice(0, SUB_PROJECTS_VISIBLE);
  const hidden = subProjects.slice(SUB_PROJECTS_VISIBLE);

  return (
    <div className="flex flex-wrap gap-1.5">
      {visible.map((sp) => (
        <SubProjectChip key={sp.id} name={sp.name} />
      ))}
      {hidden.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-xs px-2 py-0.5 rounded-full bg-bg-surface-alt text-text-muted cursor-default">
              +{hidden.length} más
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <ul className="flex flex-col gap-0.5">
              {subProjects.map((sp) => (
                <li key={sp.id}>{sp.name}</li>
              ))}
            </ul>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

interface UserRowProps {
  user: UserListItem;
  isMultiProject: boolean;
  onConfigure: (userId: number, variant: EditUserDialogVariant) => void;
}

const UserRow = ({ user, isMultiProject, onConfigure }: UserRowProps) => {
  const displayName = user.name?.trim() || user.email;
  const showEmailSubtitle = Boolean(user.name?.trim()) && user.name?.trim() !== user.email;

  return (
    <TableRow>
      <TableCell className="text-text-subtle text-sm align-top w-20">{user.id}</TableCell>
      <TableCell className="align-top">
        <div className="flex flex-col">
          <span className="font-medium text-text-default">{displayName}</span>
          {showEmailSubtitle && <span className="text-xs text-text-subtle">{user.email}</span>}
        </div>
      </TableCell>
      <TableCell className="align-top text-sm text-text-default">
        {user.phoneNumber?.trim() ? user.phoneNumber : <span className="text-text-subtle">—</span>}
      </TableCell>
      <TableCell className="align-top">
        <SubProjectsCell subProjects={user.subProjects} />
      </TableCell>
      <TableCell className="align-top text-text-subtle">—</TableCell>
      <TableCell className="align-top">
        {isMultiProject ? (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-light text-brand-navy">Sí</span>
        ) : (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-bg-surface-alt text-text-muted">No</span>
        )}
      </TableCell>
      <TableCell className="align-top">
        {user.isActive ? (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-success-light text-success">Sí</span>
        ) : (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-bg-surface-alt text-text-muted">No</span>
        )}
      </TableCell>
      <TableCell className="align-top text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label="Acciones de usuario" title="Acciones de usuario">
              <Settings />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onConfigure(user.id, 'userConfig')}>
              Configuración Usuario
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onConfigure(user.id, 'projectPermissions')}>
              Permiso por Proyecto
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

const SkeletonRows = () => (
  <>
    {SKELETON_ROW_KEYS.map((key) => (
      <TableRow key={key}>
        <TableCell>
          <Skeleton className="h-4 w-8" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-40 mb-1" />
          <Skeleton className="h-3 w-56" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-28" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-32" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-16" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-10" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-10" />
        </TableCell>
        <TableCell className="text-right">
          <Skeleton className="h-7 w-7 ml-auto rounded-md" />
        </TableCell>
      </TableRow>
    ))}
  </>
);

type DialogState = { mode: 'create' } | { mode: 'edit'; userId: number; variant: EditUserDialogVariant };

const UsersView = ({ omitHeader = false }: { omitHeader?: boolean }) => {
  const selectedProjectId = useSelectedProjectId();
  const [mode, setMode] = useState<Mode>('project');
  const [searchRaw, setSearchRaw] = useState('');
  const [subProjectFilter, setSubProjectFilter] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [dialogState, setDialogState] = useState<DialogState | null>(null);

  const search = useDebouncedValue(searchRaw.trim(), 350);

  const { data: projects } = useGetProjectsQuery();

  const selectedMasterProject = useMemo(() => {
    if (mode !== 'project' || selectedProjectId === null) return null;
    return (projects ?? []).find((p) => p.id === selectedProjectId && p.masterProjectId === null) ?? null;
  }, [projects, selectedProjectId, mode]);

  const subProjectOptions = useMemo(() => {
    const subs = selectedMasterProject?.subProjects ?? [];
    return [...subs].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
  }, [selectedMasterProject]);

  // Reset sub-project filter when the underlying master project changes.
  useEffect(() => {
    setSubProjectFilter(null);
  }, [selectedProjectId, mode]);

  const queryArg: UsersListingParams | typeof skipToken = useMemo(() => {
    if (mode === 'admins') {
      return { onlyAdmins: true, ...(search ? { search } : {}) };
    }
    if (selectedProjectId === null) return skipToken;
    return { projectId: selectedProjectId, ...(search ? { search } : {}) };
  }, [mode, selectedProjectId, search]);

  const { data, isFetching, error } = useGetUsersListingQuery(queryArg);

  const users = useMemo(() => data?.users ?? [], [data]);

  const filteredUsers = useMemo(() => {
    let arr = users;
    if (subProjectFilter !== null) {
      arr = arr.filter((u) => u.subProjects.some((sp) => sp.id === subProjectFilter));
    }
    if (activeFilter === 'active') {
      arr = arr.filter((u) => u.isActive);
    } else if (activeFilter === 'inactive') {
      arr = arr.filter((u) => !u.isActive);
    }
    return arr;
  }, [users, subProjectFilter, activeFilter]);

  const showEmptyProjectState = mode === 'project' && selectedProjectId === null;
  const showSubProjectFilter = mode === 'project' && subProjectOptions.length > 0;

  const renderTableBody = () => {
    if (isFetching) return <SkeletonRows />;
    if (users.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={8} className="text-center text-text-subtle py-8">
            No hay usuarios para mostrar.
          </TableCell>
        </TableRow>
      );
    }
    if (filteredUsers.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={8} className="text-center text-text-subtle py-8">
            No hay usuarios que coincidan con los filtros.
          </TableCell>
        </TableRow>
      );
    }
    return filteredUsers.map((user) => (
      <UserRow
        key={user.id}
        user={user}
        isMultiProject={user.isMultiProject}
        onConfigure={(userId, variant) => setDialogState({ mode: 'edit', userId, variant })}
      />
    ));
  };

  const renderContent = () => {
    if (showEmptyProjectState) {
      return (
        <div className="flex items-center justify-center px-5 py-12 bg-bg-surface border border-border-subtle rounded-xl">
          <p className="text-text-subtle font-body">
            Selecciona una inmobiliaria en la barra lateral o cambia a la vista de Admins.
          </p>
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex items-center justify-center px-5 py-12 bg-bg-surface border border-border-subtle rounded-xl">
          <p className="text-error font-body">No se pudo cargar la lista de usuarios.</p>
        </div>
      );
    }
    return (
      <div className="bg-bg-surface border border-border-subtle rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">ID</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Teléfono</TableHead>
              <TableHead>Subproyectos</TableHead>
              <TableHead>Roles</TableHead>
              <TableHead>Multi-Proyecto</TableHead>
              <TableHead>Activo</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{renderTableBody()}</TableBody>
        </Table>
      </div>
    );
  };

  return (
    <section className="flex flex-col gap-6">
      {!omitHeader && (
        <header>
          <p className="text-xs font-bold uppercase tracking-widest text-text-subtle mb-1 font-body">Configuración</p>
          <h2 className="text-2xl font-bold text-text-default m-0 font-display">Usuarios</h2>
          {mode === 'project' && selectedMasterProject && (
            <p className="text-sm text-text-subtle mt-1 font-body">{selectedMasterProject.name}</p>
          )}
        </header>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant={mode === 'project' ? 'default' : 'outline'} size="sm" onClick={() => setMode('project')}>
            Por proyecto
          </Button>
          <Button variant={mode === 'admins' ? 'default' : 'outline'} size="sm" onClick={() => setMode('admins')}>
            Admins
          </Button>
        </div>
        <Button onClick={() => setDialogState({ mode: 'create' })}>
          <Plus />
          <span>Nuevo usuario</span>
        </Button>
      </div>

      {!showEmptyProjectState && (
        <div className="flex flex-wrap items-center gap-3">
          <Input
            placeholder="Buscar por nombre, email o teléfono"
            value={searchRaw}
            onChange={(e) => setSearchRaw(e.target.value)}
            className="max-w-xs"
          />
          {showSubProjectFilter && (
            <Select
              value={subProjectFilter === null ? SUBPROJECT_ALL_VALUE : String(subProjectFilter)}
              onValueChange={(v) => setSubProjectFilter(v === SUBPROJECT_ALL_VALUE ? null : Number(v))}
            >
              <SelectTrigger className="min-w-56">
                <SelectValue placeholder="Subproyecto" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SUBPROJECT_ALL_VALUE}>Todos los subproyectos</SelectItem>
                {subProjectOptions.map((sp) => (
                  <SelectItem key={sp.id} value={String(sp.id)}>
                    {sp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={activeFilter} onValueChange={(v) => setActiveFilter(v as ActiveFilter)}>
            <SelectTrigger className="min-w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="inactive">Inactivos</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {renderContent()}

      {dialogState && (
        <UserConfigDialog
          open
          mode={dialogState.mode}
          userId={dialogState.mode === 'edit' ? dialogState.userId : undefined}
          variant={dialogState.mode === 'edit' ? dialogState.variant : undefined}
          onOpenChange={(open) => {
            if (!open) setDialogState(null);
          }}
        />
      )}
    </section>
  );
};

export default UsersView;
